import React from 'react';
import { Box, IconButton, Badge, Tooltip, Drawer, Typography, LinearProgress, List, ListItem, Button, Stack, Divider, Chip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useUploadManager } from '../contexts/UploadManagerContext';
import axiosInstance from '../services/axiosInstance';
import { formatUploadBytes, formatUploadEta, formatUploadSpeed } from '../utils/uploadUi';

const UploadCenter: React.FC = () => {
  const { uploads, activeCount, pauseUpload, resumeUpload, cancelUpload } = useUploadManager() as any;
  const [open, setOpen] = React.useState(false);
  const [aiStatus, setAiStatus] = React.useState<{queued:number; running:number; concurrency:number} | null>(null);

  const getStatusChip = (upload: any) => {
    if (upload.status === 'completed') return <Chip size="small" color="success" label="Klar" />;
    if (upload.status === 'failed') return <Chip size="small" color="error" label="Misslyckades" />;
    if (upload.status === 'paused') return <Chip size="small" color="warning" label="Pausad" />;
    if (upload.status === 'queued') return <Chip size="small" variant="outlined" label="I kö" />;
    if (upload.phase === 'processing') return <Chip size="small" color="info" label="Bearbetar" />;
    return <Chip size="small" color="primary" label="Laddar upp" />;
  };

  React.useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('dam:open-upload-center', handler as EventListener);
    return () => window.removeEventListener('dam:open-upload-center', handler as EventListener);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const fetchStatus = async () => {
      try {
        const resp = await axiosInstance.get('/api/assets/ai/status');
        setAiStatus(resp.data);
      } catch {}
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 4000);
    return () => clearInterval(id);
  }, [open]);

  return (
    <Box>
      <Tooltip title={activeCount > 0 ? `Uppladdningar pågår (${activeCount})` : 'Inga uppladdningar'}>
        <IconButton color="inherit" onClick={() => setOpen(true)} sx={{ mr: 1 }}>
          <Badge color="warning" badgeContent={activeCount} showZero={false}>
            <CloudUploadIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Drawer 
        anchor="right" 
        open={open} 
        onClose={() => setOpen(false)}
        PaperProps={{ 
          sx: { 
            width: 340,
            mt: '64px',
            height: 'calc(100% - 64px)',
            bgcolor: 'white',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.08)'
          } 
        }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 3, 
          py: 2,
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CloudUploadIcon sx={{ color: '#072f53', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#333' }}>
              Uppladdningscenter
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#666' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, py: 2, overflow: 'auto', flex: 1 }}>
          
          {/* Upload Jobs Status */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <CloudUploadIcon sx={{ color: '#072f53', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={500} sx={{ color: '#333' }}>
                Uppladdningar
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: '#666', pl: 4 }}>
              {uploads.length} jobb • {uploads.filter((u:any)=>u.status==='running').length} aktiva • {uploads.filter((u:any)=>u.status==='paused').length} pausade
            </Typography>
          </Box>

          {/* AI Queue Status */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <SmartToyIcon sx={{ color: '#072f53', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={500} sx={{ color: '#333' }}>
                AI-kö
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: '#666', pl: 4 }}>
              {aiStatus 
                ? `${aiStatus.queued} köade • ${aiStatus.running} aktiva • Max: ${aiStatus.concurrency}`
                : 'Hämtar status...'
              }
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Upload Jobs List */}
          {uploads.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#ccd6e0', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#999' }}>
                Inga aktiva uppladdningar
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {uploads.map((u: any) => (
                <ListItem 
                  key={u.id} 
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    px: 0,
                    py: 1.5,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      bgcolor: u.status === 'running' ? '#4caf50' : u.status === 'paused' ? '#ff9800' : '#bbb'
                    }} />
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#333', flex: 1 }}>
                      {u.title}
                    </Typography>
                    {getStatusChip(u)}
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {Math.round(u.overallProgress)}%
                    </Typography>
                  </Box>
                  
                  <Box sx={{ width: '100%', pl: 3 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={u.overallProgress} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 2,
                        bgcolor: '#eee',
                        mb: 1,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 2,
                          bgcolor: u.status === 'paused' ? '#ff9800' : '#072f53'
                        }
                      }} 
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                        {formatUploadBytes(u.uploadedBytes)} / {formatUploadBytes(u.totalBytes)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {u.successCount} klara • {u.failedCount} fel
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                      {u.currentFileName ? `Fil ${(u.currentFileIndex || 0) + 1} av ${u.files.length}: ${u.currentFileName}` : 'Väntar på att starta'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {u.phase === 'processing'
                          ? 'Servern bearbetar filen...'
                          : `Aktuell fil: ${Math.round(u.currentFileProgress || 0)}%`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {u.phase === 'processing'
                          ? 'Fart: -'
                          : `Fart: ${formatUploadSpeed(u.speedBps)}`}
                        {' • '}
                        {u.phase === 'processing'
                          ? 'ETA: -'
                          : `ETA: ${formatUploadEta(u.etaSeconds)}`}
                      </Typography>
                    </Box>
                    {u.lastError && (
                      <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                        Senaste fel: {u.lastError}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {(u.status === 'running') && (
                        <Button 
                          size="small" 
                          onClick={() => pauseUpload(u.id)}
                          sx={{ 
                            color: '#072f53', 
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            p: '2px 8px'
                          }}
                        >
                          Pausa
                        </Button>
                      )}
                      {(u.status === 'paused' || u.status === 'queued') && (
                        <Button 
                          size="small" 
                          onClick={() => resumeUpload(u.id)}
                          sx={{ 
                            color: '#072f53', 
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            p: '2px 8px'
                          }}
                        >
                          Återuppta
                        </Button>
                      )}
                      {!(u.status === 'completed') && (
                        <Button 
                          size="small" 
                          onClick={() => cancelUpload(u.id)}
                          sx={{ 
                            color: '#d32f2f', 
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            p: '2px 8px'
                          }}
                        >
                          Avbryt
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default UploadCenter;


